import { useNavigate } from 'react-router-dom'
import { Check, ExternalLink } from 'lucide-react'
import { useStore } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { formatHomeCurrency } from '../utils/currency'
import { COMMON_HASHTAGS, hashtagsToIntentParam } from '../utils/share'
import { showToast } from '../components/Toast'

// NOTE: チャージ完了は店舗コンテキストが無いため、二段構成シェア (#69) と
// share_logs 記録の対象外 (アプリ自体の紹介導線)。ハッシュタグだけ
// COMMON_HASHTAGS を単一ソースとして共有する。
export default function ChargeSuccess() {
  const navigate = useNavigate()
  const { wallet, lastChargeAmount, lastChargeTxHash, lastChargeExplorerLink } = useStore()
  const { t, locale } = useI18n()

  const balanceJPYC = wallet?.balanceJPYC ?? 0
  const homeCurrencyBalance = formatHomeCurrency(balanceJPYC, locale)

  const handleShare = (platform: 'instagram' | 'x' | 'line') => {
    const appUrl = window.location.origin

    switch (platform) {
      case 'instagram': {
        window.open('https://www.instagram.com/explore/tags/YOKOSOPAY/', '_blank')
        break
      }
      case 'x': {
        const params = new URLSearchParams({
          text: t('chargeSuccess.shareText'),
          url: appUrl,
          hashtags: hashtagsToIntentParam(COMMON_HASHTAGS),
        })
        window.open(`https://x.com/intent/post?${params.toString()}`, '_blank')
        break
      }
      case 'line': {
        const lineText = `${t('chargeSuccess.shareText')}\n${appUrl}`
        window.open(
          `https://line.me/R/share?text=${encodeURIComponent(lineText)}`,
          '_blank',
        )
        break
      }
    }
    showToast({ type: 'success', message: t('chargeSuccess.shared') })
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-warm-white safe-top safe-bottom">
      {/* Subtle celebration background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/2 top-[18%] h-64 w-64 -translate-x-1/2 rounded-full bg-matcha/5"
          style={{ animation: 'pulse-ring 3s ease-out infinite' }}
        />
        <div
          className="absolute left-1/4 top-[12%] h-32 w-32 -translate-x-1/2 rounded-full bg-gold/5"
          style={{ animation: 'pulse-ring 4s ease-out infinite 0.5s' }}
        />
        <div
          className="absolute right-1/4 top-[15%] h-24 w-24 translate-x-1/2 rounded-full bg-yokoso-red/5"
          style={{ animation: 'pulse-ring 3.5s ease-out infinite 1s' }}
        />
      </div>

      {/* Top spacer */}
      <div className="flex-1" />

      {/* Success Animation */}
      <div className="relative flex items-center justify-center">
        {/* Pulse ring */}
        <div
          className="absolute h-24 w-24 rounded-full bg-matcha/30"
          style={{ animation: 'pulse-ring 1.5s ease-out infinite' }}
        />
        {/* Main circle */}
        <div className="animate-scale-in flex h-24 w-24 items-center justify-center rounded-full bg-matcha ring-4 ring-matcha-light">
          <Check size={48} className="text-white" strokeWidth={3} />
        </div>
      </div>

      {/* Title */}
      <h1
        className="animate-fade-in mt-6 font-display text-2xl font-bold text-text-primary"
        style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
      >
        {t('chargeSuccess.title')}
      </h1>

      {/* Amount */}
      <p
        className="animate-fade-in mt-2 font-display text-4xl font-bold text-text-primary"
        style={{ animationDelay: '0.4s', animationFillMode: 'both' }}
      >
        &yen; {lastChargeAmount.toLocaleString()}
      </p>

      {/* New Balance Section */}
      <div
        className="animate-fade-in mx-4 mt-6 w-full max-w-sm rounded-xl bg-gray-50 p-4"
        style={{ animationDelay: '0.5s', animationFillMode: 'both' }}
      >
        <div className="flex items-center justify-between">
          <span className="font-body text-sm text-text-secondary">
            {t('chargeSuccess.newBalance')}
          </span>
          <div className="text-right">
            <span className="font-display text-lg font-bold text-matcha">
              &yen; {balanceJPYC.toLocaleString()}
            </span>
            {homeCurrencyBalance && (
              <p className="font-body text-xs text-text-tertiary">
                {homeCurrencyBalance}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* #1188 Phase 2: 有効期限告知 (資金決済法 前払式支払手段適用除外要件) */}
      {(() => {
        const EXPIRY_DAYS = 180
        const expiry = new Date()
        expiry.setDate(expiry.getDate() + EXPIRY_DAYS)
        const expiryStr = `${expiry.getFullYear()}/${(expiry.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${expiry.getDate().toString().padStart(2, '0')}`
        return (
          <div
            className="animate-fade-in mx-4 mt-4 w-full max-w-sm rounded-xl border border-yellow-200 bg-yellow-50 p-4"
            style={{ animationDelay: '0.55s', animationFillMode: 'both' }}
          >
            <p className="font-body text-xs font-semibold text-yellow-900">
              {t('chargeSuccess.expiryTitle')}
            </p>
            <p className="mt-1 font-body text-xs leading-5 text-yellow-800">
              {t('chargeSuccess.expiryBody', { date: expiryStr, days: EXPIRY_DAYS })}
            </p>
          </div>
        )
      })()}

      {/* Tx Hash (on-chain charges only) */}
      {lastChargeTxHash && (
        <div
          className="animate-fade-in mx-4 mt-4 w-full max-w-sm rounded-xl bg-gray-50 p-4"
          style={{ animationDelay: '0.6s', animationFillMode: 'both' }}
        >
          <div className="flex items-center justify-between">
            <span className="font-body text-sm text-text-secondary">
              {t('chargeSuccess.txHash')}
            </span>
            <span className="font-mono text-xs text-navy">
              {lastChargeTxHash.slice(0, 10)}...{lastChargeTxHash.slice(-6)}
            </span>
          </div>
        </div>
      )}

      {/* Block Explorer Button (on-chain charges only) */}
      {lastChargeExplorerLink && (
        <div
          className="animate-fade-in mx-4 mt-4 w-full max-w-sm"
          style={{ animationDelay: '0.7s', animationFillMode: 'both' }}
        >
          <a
            href={lastChargeExplorerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="tap-effect flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-navy/20 bg-navy/5 font-body text-sm font-medium text-navy transition-colors hover:bg-navy/10"
          >
            <ExternalLink size={16} />
            {t('chargeSuccess.viewOnExplorer')}
          </a>
        </div>
      )}

      {/* SNS Share Section */}
      <div
        className="animate-fade-in mx-4 mt-6 w-full max-w-sm"
        style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
      >
        <p className="mb-3 text-center font-display text-sm font-semibold text-text-secondary">
          {t('chargeSuccess.sharePrompt')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => handleShare('instagram')}
            className="tap-effect flex h-12 flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 font-display text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            Instagram
          </button>
          <button
            onClick={() => handleShare('x')}
            className="tap-effect flex h-12 flex-1 items-center justify-center rounded-xl bg-black font-display text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            X
          </button>
          <button
            onClick={() => handleShare('line')}
            className="tap-effect flex h-12 flex-1 items-center justify-center rounded-xl bg-[#06C755] font-display text-xs font-semibold text-white transition-opacity hover:opacity-90"
          >
            LINE
          </button>
        </div>
      </div>

      {/* Bottom spacer */}
      <div className="flex-1" />

      {/* Done Button */}
      <div
        className="animate-fade-in w-full px-4 pb-6"
        style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
      >
        <button
          onClick={() => navigate('/wallet')}
          className="tap-effect flex h-14 w-full items-center justify-center rounded-xl bg-navy font-display text-base font-semibold text-white transition-colors hover:bg-navy-dark"
        >
          {t('chargeSuccess.done')}
        </button>
      </div>
    </div>
  )
}
